/**
 * PATH: packages/drafting/src/ir/schema.ts
 * FILE_ID: DRAFTING-IR-SCHEMA-001
 * PURPOSE: The Drawing IR — the single typed JSON contract the whole
 *   Drafting Studio hangs off (AI emits it, validators check it,
 *   Konva/SVG render it, exporters consume it, versions snapshot it).
 * WHY: A typed intermediate representation lets the validator reason
 *   about geometry deterministically and keeps human edits + AI edits
 *   mutating ONE shape (see DRAFTING_STUDIO_SCOPE.md §4/§5).
 * NOTE: Runtime validation is pure type-guards in ./validate.ts (no zod
 *   dependency — the workspace locks zod v4 whose API differs; pure
 *   functions also satisfy rule 05 §1.6). Mirror this shape in the
 *   backend pydantic schema when P2/P3 land.
 */

export const IR_VERSION = '1.0'

export type Point = [number, number]

export type IrUnit = 'mm' | 'cm' | 'm' | 'in'

/** Named ISO paper size, or an explicit {w,h} in millimetres. */
export type PaperSize = 'A4' | 'A3' | 'A2' | 'A1' | 'A0' | { w: number; h: number }

export interface DrawingMeta {
  title: string
  units: IrUnit
  /** Declared drawing scale, e.g. "1:5". Drives the scale bar + title block. */
  scale: { ratio: string }
  paper: PaperSize
  grid: { size: number; snap: boolean }
  origin: Point
}

export interface Layer {
  id: string
  name: string
  /** Default fill for entities on this layer ('none' or a hex). */
  color?: string
  /** Default stroke for entities on this layer. */
  stroke?: string
  dash?: number[]
  visible?: boolean
  locked?: boolean
}

export type EntityType =
  | 'line'
  | 'polyline'
  | 'rect'
  | 'circle'
  | 'arc'
  | 'text'
  | 'dimension'
  | 'leader'
  | 'hatch'
  | 'group'

export interface EntityBase {
  id: string
  type: EntityType
  layer: string
  stroke?: string
  strokeWidth?: number
  fill?: string
  dash?: number[]
  opacity?: number
}

export interface LineEntity extends EntityBase {
  type: 'line'
  a: Point
  b: Point
}

export interface PolylineEntity extends EntityBase {
  type: 'polyline'
  points: Point[]
  closed?: boolean
}

export interface RectEntity extends EntityBase {
  type: 'rect'
  x: number
  y: number
  w: number
  h: number
}

export interface CircleEntity extends EntityBase {
  type: 'circle'
  cx: number
  cy: number
  r: number
}

export interface ArcEntity extends EntityBase {
  type: 'arc'
  cx: number
  cy: number
  r: number
  startAngle: number
  endAngle: number
}

export interface TextEntity extends EntityBase {
  type: 'text'
  at: Point
  text: string
  size?: number
  anchor?: 'start' | 'mid' | 'end'
  rotation?: number
}

export interface DimensionEntity extends EntityBase {
  type: 'dimension'
  a: Point
  b: Point
  /** Declared value; validator checks it against |a-b| * scale. */
  value?: number
  unit?: IrUnit
  offset?: number
}

export interface LeaderEntity extends EntityBase {
  type: 'leader'
  from: Point
  to: Point
  text: string
}

export interface HatchEntity extends EntityBase {
  type: 'hatch'
  points: Point[]
  pattern?: string
}

export interface GroupEntity extends EntityBase {
  type: 'group'
  children: string[]
}

export type Entity =
  | LineEntity
  | PolylineEntity
  | RectEntity
  | CircleEntity
  | ArcEntity
  | TextEntity
  | DimensionEntity
  | LeaderEntity
  | HatchEntity
  | GroupEntity

/**
 * EntityInput — an entity to ADD, with an optional id (assigned on
 * insert). Distributes over the union (`T extends any`) so each member
 * keeps its own discriminated props; a plain `Omit<Entity,'id'>` would
 * collapse to only the shared EntityBase keys.
 */
export type EntityInput = Entity extends infer T
  ? T extends Entity
    ? Omit<T, 'id'> & { id?: string }
    : never
  : never

export interface View3d {
  extrusions: Array<{ profile: string; depth: number; layer?: string }>
}

export interface DrawingIR {
  ir_version: string
  meta: DrawingMeta
  layers: Layer[]
  entities: Entity[]
  view3d?: View3d
}

/** A single validation finding. */
export interface ValidationError {
  code: string
  msg: string
  entityId?: string
  severity: 'error' | 'warn'
}

export interface ValidationReport {
  ok: boolean
  errors: ValidationError[]
}
